import { cn } from "../shared/ui/index.ts";
import { appNavigate } from "../core/app-bridge.ts";
import { Interactions } from "../core/providers/interactions.provider.tsx";
import { usePractice } from "../core/providers/practice.provider.tsx";
import {
  getNavItems,
  PATIENT_MODULE_NAV,
  paths,
} from "../core/services/navigation.service.ts";
import { I, type IconProps } from "../shared/icons/icons.tsx";
import { PatientAvatar } from "../shared/patient/patient-avatar.component.tsx";
import { PatientNameDisplay } from "../shared/patient/patient-name-display.component.tsx";

type IconCmp = (p: IconProps) => React.ReactElement;

function NotifBadge({ value, accent }: { value: number; accent: boolean }) {
  return (
    <span
      className={cn(
        "absolute top-2 right-2 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-bold leading-none",
        accent
          ? "bg-sf-primary text-white shadow-(--sf-shadow-sm)"
          : "border border-sf-border bg-white text-sf-text-secondary",
      )}
    >
      {value}
    </span>
  );
}

function ModuleTile({
  Icon,
  label,
  badge,
  accentBadge,
  onClick,
}: {
  Icon: IconCmp;
  label: string;
  badge: number | null;
  accentBadge?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="relative flex min-h-24 cursor-pointer flex-col items-start justify-between rounded-2xl border border-sf-border bg-sf-surface p-3.5 text-left shadow-(--sf-shadow-sm) active:scale-[0.98] active:bg-sf-surface-alt"
    >
      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-sf-primary-50 text-sf-primary">
        <Icon size={20} />
      </span>
      <span className="mt-2 text-[14px] font-semibold leading-tight text-sf-text">
        {label}
      </span>
      {badge != null && badge > 0 && (
        <NotifBadge value={badge} accent={!!accentBadge} />
      )}
    </button>
  );
}

function todayLabel(): string {
  return new Date().toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export function MobileDashboard() {
  const { practice } = usePractice();
  const practiceUuid = practice.uuid;
  const { current } = Interactions.usePatient();
  const items = getNavItems(practiceUuid).filter((i) => i.id !== "dashboard");

  return (
    <div
      className={cn(
        "flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden bg-sf-surface-alt",
        "pb-[max(env(safe-area-inset-bottom,0px),1rem)]",
      )}
    >
      <header className="px-4 pt-3">
        <p className="m-0 text-[12px] capitalize leading-tight text-sf-text-muted">
          {todayLabel()}
        </p>
        <h1 className="m-0 mt-0.5 text-[20px] font-bold leading-tight text-sf-text">
          Bonjour Dr Moreau
        </h1>
      </header>

      {/* Modules du patient sélectionné — en premier, avec fond dégradé
       *  pleine largeur, sans marges supplémentaires. */}
      {current && (
        <section
          aria-label="Patient sélectionné"
          className={cn(
            "mt-3 border-y border-sf-primary/30 bg-linear-to-br",
            "from-sf-primary-100 via-sf-primary-50 to-sf-primary-50/70",
            "px-3 py-3",
          )}
        >
          <header className="mb-2.5 flex items-center gap-2.5 px-1">
            <PatientAvatar patient={current} size={32} online />
            <div className="min-w-0 flex-1">
              <div className="text-[10.5px] font-bold uppercase tracking-wider text-sf-primary/80">
                Patient sélectionné
              </div>
              <PatientNameDisplay
                name={current.name}
                firstClassName="text-[14px] font-semibold leading-tight"
              />
            </div>
          </header>
          <div className="grid grid-cols-2 gap-3">
            {PATIENT_MODULE_NAV.map((m) => {
              const Icn = (I as Record<string, IconCmp>)[m.icon] ?? I.Circle;
              return (
                <ModuleTile
                  key={m.id}
                  Icon={Icn}
                  label={m.label}
                  badge={null}
                  onClick={() =>
                    appNavigate(
                      paths.patientModule(practiceUuid, current.uuid, m.id),
                    )
                  }
                />
              );
            })}
          </div>
        </section>
      )}

      {/* Modules globaux */}
      <section aria-label="Modules" className="px-3 pt-3">
        <div className="grid grid-cols-2 gap-3">
          {items.map((item) => (
            <ModuleTile
              key={item.id}
              Icon={item.icon as IconCmp}
              label={item.label}
              badge={item.badge}
              accentBadge={item.badgeColor === "primary"}
              onClick={() => appNavigate(item.path)}
            />
          ))}
        </div>
      </section>
    </div>
  );
}
